import { createContext, useContext } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../../hooks/use-toast";
import { apiService } from "../../services/api";
import {
  Account,
  AccountContactsMetadata,
  PrivacyMode,
} from "@/types/generated";

interface AccountsContextType {
  accounts: Account[] | undefined;
  isLoading: boolean;
  error: Error | null;
  createAccount: () => Promise<{ id: string; qrImage: string }>;
  loginAccount: (id: string) => Promise<{ image: string }>;
  logoutAccount: (id: string) => Promise<void>;
  syncAccount: (id: string) => Promise<void>;
  updatePrivacyMode: (id: string, mode: PrivacyMode) => Promise<void>;
  contactsMetadata: AccountContactsMetadata | undefined;
}

interface AccountsProviderProps {
  children: React.ReactNode;
}

const AccountsContext = createContext<AccountsContextType | undefined>(
  undefined
);

export const useAccounts = () => {
  const context = useContext(AccountsContext);
  if (!context) {
    throw new Error("useAccounts must be used within an AccountsProvider");
  }
  return context;
};

export const AccountsProvider = ({ children }: AccountsProviderProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: accounts,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["accounts"],
    queryFn: () => apiService.getAccounts(),
  });

  const { data: contactsMetadata } = useQuery({
    queryKey: ["contacts", "metadata"],
    queryFn: async () => {
      if (!accounts?.data?.[0]) return undefined;
      const response = await apiService.getAccountContactsMetadata(
        accounts.data[0].id
      );
      return response.data;
    },
    enabled: !!accounts?.data?.[0],
  });

  const createAccountMutation = useMutation({
    mutationFn: () => apiService.createAccount(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({
        title: "Success",
        description: "Account created successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to create account",
      });
    },
  });

  const loginAccountMutation = useMutation({
    mutationFn: (id: string) => apiService.loginAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({
        title: "Success",
        description: "Account logged in successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to login account",
      });
    },
  });

  const logoutAccountMutation = useMutation({
    mutationFn: (id: string) => apiService.logoutAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({
        title: "Success",
        description: "Account logged out successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to logout account",
      });
    },
  });

  const syncAccountMutation = useMutation({
    mutationFn: (id: string) => apiService.syncAccount(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({
        title: "Success",
        description: "Account synced successfully",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error instanceof Error ? error.message : "Failed to sync account",
      });
    },
  });

  const createAccount = async () => {
    const result = await createAccountMutation.mutateAsync();
    return {
      id: result.data.account.id,
      qrImage: result.data.image,
    };
  };

  const loginAccount = async (id: string) => {
    const result = await loginAccountMutation.mutateAsync(id);
    return result.data;
  };

  const logoutAccount = async (id: string) => {
    await logoutAccountMutation.mutateAsync(id);
  };

  const syncAccount = async (id: string) => {
    await syncAccountMutation.mutateAsync(id);
  };

  const updatePrivacyModeMutation = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: PrivacyMode }) =>
      apiService.updateAccountSettings(id, { privacy_mode: mode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounts"] });
      toast({
        title: "הצלחה",
        description: "הגדרות הפרטיות עודכנו בהצלחה",
      });
    },
    onError: (error) => {
      toast({
        variant: "destructive",
        title: "שגיאה",
        description:
          error instanceof Error
            ? error.message
            : "שגיאה בעדכון הגדרות הפרטיות",
      });
    },
  });

  const updatePrivacyMode = async (id: string, mode: PrivacyMode) => {
    await updatePrivacyModeMutation.mutateAsync({ id, mode });
  };

  return (
    <AccountsContext.Provider
      value={{
        accounts: accounts?.data,
        isLoading,
        error,
        createAccount,
        loginAccount,
        logoutAccount,
        syncAccount,
        updatePrivacyMode,
        contactsMetadata,
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
};
